/**
 * mmc_nav_categories.js
 * Navigation component with category grouping and search
 */

import React, { useState, useMemo, useEffect } from 'react';
import { CaretDown, MagnifyingGlass } from '@phosphor-icons/react';
import { get_icon, get_category_icon } from '../utils/icon_map';
import { MmcInput, MmcLabel, MmcBadge } from './ui';


const MmcNavCategories = ({ modules = [], onModuleSelect = () => { }, activeTab = null }) => {
  const [search_query, set_search_query] = useState('');
  const [expanded_categories, set_expanded_categories] = useState({});

  const grouped_modules = useMemo(() => {
    const grouped = {}; // Internal grouping using lowercase keys
    const category_display_names = {}; // Map lowercase key to original casing

    // 1. Map modules and aggregate category metadata
    modules
      .filter(m => m.enabled !== false)
      .forEach(module => {
        const raw_category = module.metadata?.category || 'General';
        const category_key = raw_category.toLowerCase();

        // Pick up category-level metadata from the module manifest
        const category_icon = module.metadata?.category_icon;
        const category_order = module.metadata?.category_order;

        if (!grouped[category_key]) {
          // First time seeing this category (case-insensitive)
          category_display_names[category_key] = raw_category;
          grouped[category_key] = {
            items: [],
            icon: category_icon,
            order: category_order ?? 999
          };
        } else {
          // If this module has more specific category metadata, adopt it
          // We prioritize the lowest order and the first defined icon
          if (!grouped[category_key].icon && category_icon) {
            grouped[category_key].icon = category_icon;
          }
          if (category_order !== undefined && category_order < grouped[category_key].order) {
            grouped[category_key].order = category_order;
          }
        }

        grouped[category_key].items.push(module);
      });

    // 2. Sort modules within categories
    Object.values(grouped).forEach(group => {
      group.items.sort((a, b) => (a.order || 999) - (b.order || 999));
    });

    // 3. Sort entries and reconstruct result with display names
    const result = {};
    Object.keys(grouped)
      .sort((a, b) => {
        // Sort by aggregate category order, then by display name
        if (grouped[a].order !== grouped[b].order) return grouped[a].order - grouped[b].order;
        return category_display_names[a].localeCompare(category_display_names[b]);
      })
      .forEach(key => {
        result[category_display_names[key]] = grouped[key];
      });

    return result;
  }, [modules]);

  const filtered_modules = useMemo(() => {
    if (!search_query.trim()) return grouped_modules;
    const query = search_query.toLowerCase();
    const filtered = {};
    Object.entries(grouped_modules).forEach(([category, group]) => {
      const filtered_items = group.items.filter(
        m => m.display_name.toLowerCase().includes(query) || category.toLowerCase().includes(query)
      );
      if (filtered_items.length > 0) filtered[category] = { ...group, items: filtered_items };
    });
    return filtered;
  }, [grouped_modules, search_query]);

  useEffect(() => {
    if (search_query.trim()) {
      const all_expanded = {};
      Object.keys(filtered_modules).forEach(cat => all_expanded[cat] = true);
      set_expanded_categories(all_expanded);
    }
  }, [search_query, filtered_modules]);

  // Expand category of active tab
  useEffect(() => {
    if (activeTab) {
      const activeModule = modules.find(m => m.id === activeTab);
      const category = activeModule?.metadata?.category || 'General';
      set_expanded_categories(prev => ({ ...prev, [category]: true }));
    }
  }, [activeTab, modules]);

  return (
    <div className="mmc-nav-categories">
      <div className="mmc-nav-search mmc-stack">
        <MmcInput
          placeholder="SEARCH TOOLS..."
          icon={MagnifyingGlass}
          value={search_query}
          onChange={(e) => set_search_query(e.target.value)}
          aria-label="Search tools"
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '-8px', padding: '0 4px' }}>
          <MmcLabel style={{ fontSize: '0.65rem', color: 'var(--mmc-clr-text-muted)' }}>Quick search</MmcLabel>
          <MmcBadge style={{ fontSize: '0.6rem' }} aria-hidden="true">⌘ K</MmcBadge>
        </div>
      </div>

      <div className="mmc-categories-list" role="list">
        {Object.entries(filtered_modules).map(([category, group]) => (
          <div key={category} className={`mmc-category-group ${expanded_categories[category] ? 'open' : ''}`} role="listitem">
            <button
              className="mmc-category-header"
              onClick={() => set_expanded_categories(prev => ({ ...prev, [category]: !prev[category] }))}
              aria-expanded={!!expanded_categories[category]}
            >
              <div className="mmc-category-header-left">
                <span className="mmc-category-header-icon" aria-hidden="true">
                  {get_category_icon(category, group.icon)}
                </span>
                <span>{category}</span>
              </div>
              <CaretDown size={14} weight="bold" className="mmc-category-icon" aria-hidden="true" />
            </button>

            {expanded_categories[category] && (
              <ul className="mmc-category-items">
                {group.items.map(module => (
                  <li key={module.id}>
                    <button
                      className={`mmc-category-item-button ${activeTab === module.id ? 'active' : ''}`}
                      onClick={() => onModuleSelect(module.id)}
                      aria-current={activeTab === module.id ? 'page' : undefined}
                    >
                      <span className="mmc-item-icon" aria-hidden="true">
                        {get_icon(module.icon || 'cube', 18, "bold")}
                      </span>
                      <span className="mmc-item-text">{module.display_name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MmcNavCategories;
